/**
 * Descope, if this build has a project — the session half of sign-in.
 *
 * `lib/firebase.ts`'s shape, deliberately: absent is a supported state, the SDK
 * loads lazily, and what leaves here is a token and an id, never an SDK object.
 * `lib/session.ts` picks between the two; nothing else imports this module
 * except the sign-in screen, which mounts the flow.
 *
 * ## One project, a tenant per brand
 *
 * Accounts are per brand (`docs/plans/multi-brand-accounts.md` §3a): Descope's
 * Tenant User Isolation makes the same email two unconnected users in two
 * tenants. So the flow is started **with the brand's tenant**, and a session
 * whose `dct` names another one is treated as signed out here — qv-data would
 * accept it as the other brand's account, which is never what this build means.
 *
 * ## The flow writes the session; this module reads it
 *
 * `<descope-wc>` runs its own SDK with `persistTokens` on, so a finished flow
 * has already stored the refresh token. `adoptSession` then asks this SDK to
 * `refresh()` from storage, which is one request at sign-in and keeps this
 * module off the component's internals.
 *
 * ## The id reported is Descope's, not the account's
 *
 * `onAuthChanged` reports `descope:<sub>` — qv-data's own spelling. The
 * account key is a ULID minted server-side at first sight, so `lib/user.ts`
 * asks `/auth/me` for it; with Firebase the two were the same string.
 */

import type { createSdk } from "@descope/web-js-sdk";

import { BRAND } from "./brand-current";

/** Built with `persistTokens`, which is what types the token getters in. */
function build(create: typeof createSdk, projectId: string) {
  return create({ projectId, persistTokens: true, autoRefresh: true });
}

type Sdk = ReturnType<typeof build>;

const PROJECT_ID = (import.meta.env.VITE_DESCOPE_PROJECT_ID as string | undefined) || "";

/** Whether this build signs in through Descope. */
export const descopeConfigured: boolean = Boolean(PROJECT_ID);

/** The project, for the flow component's `project-id`. */
export const descopeProjectId: string = PROJECT_ID;

/** The flow every brand signs in with: email code or passkey. */
export const DESCOPE_FLOW = "sign-up-or-in";

/** The flow behind "Add a passkey?" — its screen holds the offer's buttons. */
export const PASSKEY_FLOW = "add-passkeys";

/**
 * Builds that sign into **another brand's** tenant.
 *
 * TrendStrat is the test host for SweeterTrade (owner, 2026-10-10:
 * `app.trendstrat.com` tests SweeterTrade), so its accounts are SweeterTrade
 * accounts and get the `st-*` plans. Every other build's tenant is its key.
 */
const TENANT_OF: Readonly<Record<string, string>> = { trendstrat: "sweetertrade" };

/** The Descope tenant — and qv-data's `app_key` — of this build's accounts. */
export const descopeTenant: string = TENANT_OF[BRAND.key] ?? BRAND.key;

/**
 * The Descope **style** the flow draws in — named after the tenant, so the logo
 * and colours inside the form follow the brand whose accounts it makes. Styles
 * are per project, not per tenant, so each one is created by hand in the
 * console (Styles); one that does not exist there is a broken sign-in screen.
 */
export const descopeStyle: string = descopeTenant;

/** qv-data's prefix for a Descope uid (`app/user/identity.py`). */
const UID_PREFIX = "descope:";

let sdkPromise: Promise<Sdk> | null = null;
let uid: string | null = null;
let settled = false;
const listeners = new Set<(uid: string | null) => void>();

function publish(next: string | null): void {
  if (settled && next === uid) return;
  uid = next;
  settled = true;
  for (const notify of listeners) notify(uid);
}

/** The JWT's payload, unverified — only to read who and which tenant. */
function claimsOf(token: string | null | undefined): Record<string, unknown> | null {
  const body = token?.split(".")[1];
  if (!body) return null;
  try {
    const json = atob(body.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * The uid a session token stands for, or `null`.
 *
 * `dct` is the tenant a flow signed into; a raw-API token carries only
 * `tenants`, whose single key is the same answer under isolation. Either way it
 * must be this build's, for the module docstring's reason.
 */
export function uidOf(token: string | null | undefined, tenant: string = descopeTenant): string | null {
  const claims = claimsOf(token);
  if (!claims || typeof claims.sub !== "string" || !claims.sub) return null;
  const tenants = Object.keys((claims.tenants as Record<string, unknown> | undefined) ?? {});
  const current = typeof claims.dct === "string" ? claims.dct : tenants.length === 1 ? tenants[0] : null;
  if (current !== tenant) return null;
  return UID_PREFIX + claims.sub;
}

async function descope(): Promise<Sdk> {
  if (!descopeConfigured) throw new Error("Descope is not configured");
  if (!sdkPromise) {
    sdkPromise = (async () => {
      const { default: create } = await import("@descope/web-js-sdk");
      const sdk = build(create, PROJECT_ID);
      sdk.onSessionTokenChange((token) => publish(uidOf(token)));
      // Restore: a stored refresh token is a returning user. None is an
      // answer too, and it has to be published — see `onAuthChanged`.
      if (sdk.getRefreshToken()) {
        try {
          await sdk.refresh();
        } catch {
          // An unreachable Descope is signed out, not a stuck spinner.
        }
      }
      publish(uidOf(sdk.getSessionToken()));
      return sdk;
    })();
  }
  return sdkPromise;
}

/**
 * Who is signed in, as it changes — `firebase.ts`'s contract: the callback
 * fires once with the answer even when nobody is, so the gate can tell
 * "checking" from "signed out".
 */
export function onAuthChanged(callback: (uid: string | null) => void): () => void {
  listeners.add(callback);
  if (settled) queueMicrotask(() => listeners.has(callback) && callback(uid));
  else void descope().catch(() => publish(null));
  return () => {
    listeners.delete(callback);
  };
}

/**
 * A current session token, or `null`.
 *
 * Refreshed here when it has expired: `autoRefresh` runs on a timer, and a tab
 * that slept through it would otherwise send a dead token and read the 401 as
 * having been signed out.
 */
export async function idToken(): Promise<string | null> {
  if (!descopeConfigured) return null;
  const sdk = await descope();
  let token = sdk.getSessionToken();
  if (!token || sdk.isJwtExpired(token)) {
    if (!sdk.getRefreshToken()) return null;
    await sdk.refresh().catch(() => undefined);
    token = sdk.getSessionToken();
  }
  return uidOf(token) ? token : null;
}

/**
 * The signed-in account's email, or `null` — asked of Descope's user record,
 * never guessed. `email` when set, else the first login id that is one (an
 * email-code sign-in's login id is the address).
 */
export async function signedInEmail(): Promise<string | null> {
  if (!descopeConfigured) return null;
  try {
    const sdk = await descope();
    if (!uidOf(sdk.getSessionToken())) return null;
    const me = await sdk.me();
    if (!me.ok || !me.data) return null;
    const user = me.data as { email?: string; loginIds?: string[] };
    if (user.email) return user.email;
    return user.loginIds?.find((id) => id.includes("@")) ?? null;
  } catch {
    return null;
  }
}

/** After the flow says `success`: read the session it stored. */
export async function adoptSession(): Promise<void> {
  const sdk = await descope();
  await sdk.refresh();
  publish(uidOf(sdk.getSessionToken()));
}

export async function signOut(): Promise<void> {
  if (!descopeConfigured) return;
  const sdk = await descope();
  // Revokes the refresh token server-side and clears storage; a failure still
  // signs this tab out, because staying signed in after asking not to is worse.
  await sdk.logout().catch(() => undefined);
  publish(null);
}

/**
 * The one domain passkeys work on, with its subdomains: this build's project's
 * passkey **Top Level Domain** (Authentication Methods › Passkeys), one per
 * project. Set per brand by `ts/scripts/build-prod.mjs`; empty — a dev build
 * without it — offers no passkey anywhere.
 */
export const PASSKEY_DOMAIN: string =
  (import.meta.env.VITE_DESCOPE_PASSKEY_DOMAIN as string | undefined)?.trim() || "";

/**
 * Whether this host can make a passkey at all. A host off the domain cannot —
 * the browser refuses a passkey for a domain the page is not on — so offering
 * one there is a button that always fails.
 */
export function passkeysWorkHere(hostname: string, domain: string = PASSKEY_DOMAIN): boolean {
  if (!domain) return false;
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

/**
 * Whether to offer "Add a passkey?" — this host is on the passkey domain, the
 * account has none and this device can make one.
 *
 * Asked of Descope (`me().webauthn`) rather than remembered here: a passkey
 * added on another device, or by the flow itself, means no prompt. Only an
 * explicit `false` offers — the field is on Descope's user record (checked on
 * `qufolio-dev`, 2026-10-10) but not in the SDK's type, so absent is unknown.
 */
export async function canAddPasskey(): Promise<boolean> {
  if (!passkeysWorkHere(location.hostname)) return false;
  const sdk = await descope();
  if (!(await sdk.webauthn.helpers.isSupported())) return false;
  const me = await sdk.me();
  const user = me.data as (typeof me.data & { webauthn?: boolean }) | undefined;
  return Boolean(me.ok && user?.webauthn === false && user.loginIds?.length);
}
